import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getApiBaseUrl, requestAgentBridgeUrl } from '../../services/api';

/*
 * AI cost & margin: the provider the open models run on, every model in use
 * with its cost price, margin and sell price and where the price came from,
 * 30 days of usage per model, and the check of our cost against the provider's
 * own bill. Rendered inside the Command Deck, so it uses the deck's cd- classes.
 */

interface Usage {
  calls: number;
  inputTokens: number;
  outputTokens: number;
  cacheTokens: number;
  ourCostUsd: number;
  chargedUsd: number;
}

interface Model {
  value: string;
  label: string;
  provider: string;
  tier: string;
  billing: 'per-token' | 'flat-fee' | 'none';
  basis: string;
  costInput: number | null;
  costOutput: number | null;
  sellInput: number | null;
  sellOutput: number | null;
  markupPercent: number | null;
  modelMarginPercent: number | null;
  priceSource: string | null;
  priceVerifiedAt: string | null;
  warnings: string[];
  usage30d: Usage;
}

interface Provider {
  key: string;
  label: string;
  kind: 'vendor' | 'open-host';
  billingMode: 'per-token' | 'subscription';
  enabled: boolean;
  isActive: boolean;
  hasKey: boolean;
  enabledModels: number;
}

interface BillRow {
  providerKey: string;
  period: 'day' | 'month';
  periodStart: string;
  billedUsd: number;
  ourCostUsd: number;
  gapUsd: number;
  gapPercent: number | null;
  complete: boolean;
  status: 'match' | 'check' | 'open';
}

interface Report {
  available: boolean;
  sellPricing: boolean;
  marginPercent: number;
  providers: Provider[];
  models: Model[];
  usedButNotListed: Array<{ value: string; usage30d: Usage }>;
  unpricedInUsage: Array<{ model: string; calls: number; lastAt: string }>;
  billCheck: BillRow[];
  pricesRefreshedAt: string | null;
}

const API = () => `${getApiBaseUrl()}/super/billing/ai-models`;
const STALE_MS = 7 * 86_400_000;

const usd = (n: number) => {
  const abs = Math.abs(n);
  const text = `$${abs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: abs !== 0 && abs < 1 ? 4 : 2 })}`;
  if (n < 0) return `-${text}`;
  return text;
};
const rate = (n: number | null) => (n == null ? 'not set' : `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`);
const tokens = (n: number) => {
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return String(n);
};
const day = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' });
const month = (iso: string) => new Date(iso).toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });
const when = (iso: string) => new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

const errorText = (err: unknown, fallback: string) => {
  const detail = (err as any)?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  return fallback;
};

const SOURCE_LABEL: Record<string, string> = {
  'provider-api': 'Provider API',
  feed: 'LiteLLM price list',
  manual: 'Typed in by hand',
  subscription: 'Flat monthly plan',
};

const PriceSource: React.FC<{ m: Model }> = ({ m }) => {
  if (m.billing === 'none') return <span className="cd-chip r">No price</span>;
  const label = SOURCE_LABEL[m.priceSource || ''] || 'Unknown source';
  if (m.priceSource === 'manual') return <span className="cd-chip w">{label}</span>;
  if (!m.priceVerifiedAt) return <span className="cd-chip w">{label} · never checked</span>;
  const stale = Date.now() - new Date(m.priceVerifiedAt).getTime() > STALE_MS;
  return <span className={`cd-chip ${stale ? 'w' : 'g'}`}>{label} · {day(m.priceVerifiedAt)}</span>;
};

const BILL_CHIP: Record<BillRow['status'], { tone: string; label: string }> = {
  match: { tone: 'g', label: 'Match' },
  check: { tone: 'r', label: 'Check' },
  open: { tone: 'o', label: 'Still open' },
};

const MarginCell: React.FC<{ m: Model; platformMargin: number; onSaved: () => void }> = ({ m, platformMargin, onSaved }) => {
  const [draft, setDraft] = useState(m.modelMarginPercent == null ? '' : String(m.modelMarginPercent));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setDraft(m.modelMarginPercent == null ? '' : String(m.modelMarginPercent));
  }, [m.modelMarginPercent]);

  if (m.basis === 'fixed-price' || m.basis === 'flat-fee-fixed-price') {
    return <span className="cd-sub2">Own sell price</span>;
  }
  if (m.billing === 'none') return <span className="cd-sub2">Needs a price</span>;

  const save = async () => {
    const trimmed = draft.trim();
    const current = m.modelMarginPercent == null ? '' : String(m.modelMarginPercent);
    if (trimmed === current) return;
    const value = trimmed === '' ? null : Number(trimmed);
    if (value != null && (!Number.isFinite(value) || value < 0 || value > 500)) {
      setError('0 to 500');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await axios.put(`${API()}/${encodeURIComponent(m.value)}/margin`, { margin_percent: value });
      onSaved();
    } catch (err) {
      setError(errorText(err, 'Not saved'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <input
          id={`margin-${m.value}`}
          className="aicm-input"
          inputMode="decimal"
          value={draft}
          placeholder={String(platformMargin)}
          aria-label={`Margin for ${m.label}, empty uses the default ${platformMargin}%`}
          disabled={saving}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        />
        <span className="cd-sub2">%</span>
        {saving && <Loader2 className="animate-spin" size={12} />}
        {!saving && m.modelMarginPercent == null && <span className="cd-chip o">default</span>}
        {!saving && m.modelMarginPercent != null && <span className="cd-chip b">custom</span>}
      </span>
      {error && <span style={{ fontSize: 11, color: 'var(--cd-red)' }}>{error}</span>}
    </div>
  );
};

const CSS = `
.aicm-input{width:58px;text-align:right;font-family:inherit;font-size:12px;padding:4px 6px;border-radius:6px;border:1px solid var(--cd-line2);background:var(--cd-panel);color:var(--cd-ink)}
.aicm-input:focus{outline:2px solid var(--cd-blue);outline-offset:1px}
.aicm-t{min-width:1180px}
.aicm-t th,.aicm-t td{white-space:nowrap;vertical-align:middle}
.aicm-t td.wrap{white-space:normal;min-width:190px}
.aicm-prov{display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:10px;padding:12px 15px}
.aicm-card{border:1px solid var(--cd-line);border-radius:10px;padding:12px;display:flex;flex-direction:column;gap:4px;min-width:0}
.aicm-card.live{border-color:var(--cd-good);background:var(--cd-good-soft)}
`;

const AiCostMarginTab: React.FC = () => {
  const navigate = useNavigate();
  const [report, setReport] = useState<Report | null>(null);
  const [failed, setFailed] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [refreshNote, setRefreshNote] = useState('');
  const [keysError, setKeysError] = useState('');

  const load = useCallback(async () => {
    setFailed('');
    try {
      const { data } = await axios.get(API());
      setReport(data);
    } catch (err) {
      setFailed(errorText(err, 'Could not load AI models.'));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    setRefreshNote('');
    try {
      await axios.post(`${API()}/refresh`);
      await load();
      setRefreshNote('Prices and provider bills refreshed.');
    } catch (err) {
      setRefreshNote(errorText(err, 'Refresh failed.'));
    } finally {
      setRefreshing(false);
    }
  };

  // The tab opens before the await: a window.open after it is treated as a popup and blocked.
  const openKeys = async () => {
    setKeysError('');
    const tab = window.open('', '_blank');
    try {
      const { url } = await requestAgentBridgeUrl('/admin/settings');
      if (!url) {
        tab?.close();
        return;
      }
      if (!tab) {
        window.location.assign(url);
        return;
      }
      tab.opener = null;
      tab.location.href = url;
    } catch (err) {
      tab?.close();
      setKeysError(errorText(err, 'Could not open the builder.'));
    }
  };

  const totals = useMemo(() => (report?.models || []).reduce((t, m) => ({
    calls: t.calls + m.usage30d.calls,
    cost: t.cost + m.usage30d.ourCostUsd,
    charged: t.charged + m.usage30d.chargedUsd,
  }), { calls: 0, cost: 0, charged: 0 }), [report]);

  if (failed) return <div className="cd-note"><b>Couldn’t load AI models.</b> {failed}</div>;
  if (!report) return <div style={{ padding: 30, display: 'grid', placeItems: 'center' }}><Loader2 className="animate-spin" size={16} /></div>;
  if (report.available === false) return <div className="cd-note"><b>Builder unreachable.</b> Providers, models and prices live on the builder and appear once it answers.</div>;

  const profit = totals.charged - totals.cost;
  const marginPct = totals.cost > 0 ? `${((profit / totals.cost) * 100).toFixed(1)}%` : 'n/a';
  const openLane = report.providers.find((p) => p.kind === 'open-host' && p.isActive && p.enabled);
  const providers = [...report.providers].sort((a, b) => Number(b.isActive) - Number(a.isActive) || Number(b.enabled) - Number(a.enabled));
  const bills = report.billCheck.filter((b) => b.period === 'day').slice(0, 14);
  const monthlyBills = report.billCheck.filter((b) => b.period === 'month').slice(0, 4);

  return (
    <>
      <style>{CSS}</style>

      {!report.sellPricing && (
        <div className="cd-note"><b>Margins are not being charged yet.</b> The builder runs without UNIFIED_SELL_PRICING=1, so clients pay our cost. Sell prices below show what they would pay once it is on.</div>
      )}

      <div className="cd-kpis">
        <div className="cd-kpi"><div className="lab"><span className="cd-eyebrow">AI cost · 30 days</span></div><div className="v">{usd(totals.cost)}</div><div className="s">what providers cost us</div></div>
        <div className="cd-kpi"><div className="lab"><span className="cd-eyebrow">Charged · 30 days</span></div><div className="v">{usd(totals.charged)}</div><div className="s">{totals.calls.toLocaleString()} calls</div></div>
        <div className="cd-kpi"><div className="lab"><span className="cd-eyebrow">Profit · 30 days</span></div><div className={`v ${profit < 0 ? 'red' : 'good'}`}>{usd(profit)}</div><div className="s">margin {marginPct}</div></div>
        <div className="cd-kpi"><div className="lab"><span className="cd-eyebrow">Default AI margin</span></div><div className="v">{report.marginPercent}%</div><div className="s"><button type="button" className="cd-link" onClick={() => navigate('/super/plan-management')}>Change in Plans &amp; Payments</button></div></div>
      </div>

      <div className="cd-panel">
        <div className="cd-ph">
          <span className="t">Providers</span>
          <button type="button" className="cd-btn sm" onClick={openKeys}>Add or switch in builder Keys</button>
        </div>
        {keysError && <div className="cd-sub2" style={{ padding: '8px 15px 0', color: 'var(--cd-red)' }}>{keysError}</div>}
        {!openLane && <div className="cd-note" style={{ margin: '12px 15px 0' }}><b>No open-model provider is connected.</b> Open models cannot run until one is connected in builder Keys.</div>}
        <div className="aicm-prov">
          {providers.map((p) => (
            <div key={p.key} className={`aicm-card${p.isActive && p.kind === 'open-host' ? ' live' : ''}`}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', fontWeight: 600 }}>
                {p.label}
                {p.isActive && p.kind === 'open-host' && <span className="cd-chip g">Open models run here</span>}
                {p.kind === 'vendor' && <span className="cd-chip b">Flagship</span>}
                {!p.enabled && <span className="cd-chip o">Off</span>}
                {p.billingMode === 'subscription' && <span className="cd-chip r">Monthly plan, not supported</span>}
              </div>
              <span className="cd-sub2">{p.billingMode === 'per-token' ? 'Billed per token' : 'Flat monthly plan'} · {p.hasKey ? 'key saved' : 'no key'} · {p.enabledModels} {p.enabledModels === 1 ? 'model' : 'models'} on</span>
            </div>
          ))}
        </div>
      </div>

      <div className="cd-panel">
        <div className="cd-ph">
          <span className="t">Models in use</span>
          <span className="cd-sub2">Per million tokens · leave a margin empty to use the default {report.marginPercent}%</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="cd-t aicm-t">
            <thead><tr>
              <th>Model</th><th>Price source</th><th className="r">Cost in · out</th><th className="r">Margin</th><th className="r">Sell in · out</th>
              <th className="r">Calls</th><th className="r">Tokens</th><th className="r">Our cost</th><th className="r">Charged</th><th className="r">Profit</th>
            </tr></thead>
            <tbody>
              {report.models.length === 0 && <tr><td colSpan={10} className="cd-sub2">No models are switched on. Turn models on in builder Admin, Settings, Swarm.</td></tr>}
              {report.models.map((m) => {
                const u = m.usage30d;
                const rowProfit = u.chargedUsd - u.ourCostUsd;
                const flagged = m.warnings.filter((w) => /loses|\$0|not charged|no cost|not set up/i.test(w));
                return (
                  <tr key={m.value}>
                    <td className="wrap">
                      {m.label}
                      <div className="cd-sub2">{m.provider}{m.tier === 'flagship' ? ' · flagship' : ''}</div>
                      {flagged.map((w) => <div key={w} style={{ fontSize: 11, color: 'var(--cd-red)' }}>{w}</div>)}
                    </td>
                    <td><PriceSource m={m} /></td>
                    <td className="r m">{rate(m.costInput)} · {rate(m.costOutput)}</td>
                    <td className="r"><MarginCell m={m} platformMargin={report.marginPercent} onSaved={load} /></td>
                    <td className="r m">{rate(m.sellInput)} · {rate(m.sellOutput)}</td>
                    <td className="r m">{u.calls.toLocaleString()}</td>
                    <td className="r m">{tokens(u.inputTokens + u.outputTokens + u.cacheTokens)}<div className="cd-sub2">{tokens(u.cacheTokens)} cached</div></td>
                    <td className="r m">{usd(u.ourCostUsd)}</td>
                    <td className="r m">{usd(u.chargedUsd)}</td>
                    <td className="r m" style={{ fontWeight: 600, color: rowProfit < 0 ? 'var(--cd-red)' : 'var(--cd-good)' }}>{usd(rowProfit)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {(report.usedButNotListed.length > 0 || report.unpricedInUsage.length > 0) && (
          <div style={{ padding: '10px 15px 14px', fontSize: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {report.usedButNotListed.length > 0 && (
              <div>
                <b>Used in the last 30 days but now switched off</b>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                  {report.usedButNotListed.map((u) => <span key={u.value} className="cd-chip o">{u.value} · {u.usage30d.calls} calls · {usd(u.usage30d.ourCostUsd)} cost</span>)}
                </div>
              </div>
            )}
            {report.unpricedInUsage.length > 0 && (
              <div>
                <b>Used without a price</b> <span className="cd-sub2">(those calls were charged $0)</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                  {report.unpricedInUsage.map((u) => <span key={u.model} className="cd-chip r">{u.model} · {u.calls} calls · last {when(u.lastAt)}</span>)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="cd-panel">
        <div className="cd-ph">
          <span className="t">Bill check</span>
          <span style={{ display: 'inline-flex', gap: 10, alignItems: 'center' }}>
            <span className="cd-sub2">{report.pricesRefreshedAt ? `Prices and bills refreshed ${when(report.pricesRefreshedAt)}` : 'Not refreshed yet'}</span>
            <button type="button" className="cd-btn sm" disabled={refreshing} onClick={refresh}>
              {refreshing ? <Loader2 className="animate-spin" size={12} /> : <RefreshCw size={12} />} Refresh now
            </button>
          </span>
        </div>
        {refreshNote && <div className="cd-sub2" style={{ padding: '8px 15px 0' }}>{refreshNote}</div>}
        {report.billCheck.length === 0 && (
          <div className="cd-sub2" style={{ padding: 15 }}>No provider bills pulled yet. Fireworks bills come in per day and DeepInfra per month, once a day.</div>
        )}
        {report.billCheck.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table className="cd-t">
              <thead><tr><th>Provider</th><th>Period</th><th className="r">Our cost</th><th className="r">Provider billed</th><th className="r">Gap</th><th>Status</th></tr></thead>
              <tbody>
                {[...monthlyBills, ...bills].map((b) => (
                  <tr key={`${b.providerKey}-${b.period}-${b.periodStart}`}>
                    <td>{b.providerKey}</td>
                    <td>{b.period === 'month' ? month(b.periodStart) : day(b.periodStart)}</td>
                    <td className="r m">{usd(b.ourCostUsd)}</td>
                    <td className="r m">{usd(b.billedUsd)}</td>
                    <td className="r m">{usd(b.gapUsd)}{b.gapPercent != null && <div className="cd-sub2">{b.gapPercent > 0 ? '+' : ''}{b.gapPercent}%</div>}</td>
                    <td><span className={`cd-chip ${BILL_CHIP[b.status].tone}`}>{BILL_CHIP[b.status].label}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="cd-sub2" style={{ padding: '10px 15px 14px' }}>A gap above 5% or $5 a day, or 2% or $5 a month, is marked Check. Provider totals leave out credits, discounts and taxes.</div>
      </div>

      <div className="cd-note">
        <b>Usage by client and by call.</b> Every company’s usage, and each call with its tokens, cost and charge, is on <button type="button" className="cd-link" onClick={() => navigate('/super/machine/usage')}>Usage &amp; charging</button>.
      </div>
    </>
  );
};

export default AiCostMarginTab;
